import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-doc-viewer',
  templateUrl: './doc-viewer.component.html',
  styleUrls: ['./doc-viewer.component.scss'],
})
export class DocViewerComponent {
  src: any;
  title: any;
  info: any;
  viewer: any;
  docViewerLoaded = false;
  error: string | null = null;

  constructor(@Inject(MAT_DIALOG_DATA) data) {
    const { title, src, info, viewer } = data;
    this.title = title;
    this.info = info;
    this.src = src;
    this.viewer = viewer;
    console.log('data: ', data);
  }

  isPDF(): boolean {
    if (!this.src) return false;

    // Check if the viewer type is PDF
    if (this.viewer === 'pdf') return true;

    // Check if the URL ends with .pdf
    if (typeof this.src === 'string' && this.src.toLowerCase().endsWith('.pdf')) return true;

    // Check if the content type is PDF
    if (this.info && this.info.contentType === 'application/pdf') return true;

    return false;
  }

  onDocumentLoaded(event) {
    console.log('event: ', event);
    this.docViewerLoaded = true;
    this.error = null;
  }

  handleError(event: any) {
    console.error('Error loading document:', event);
    this.error = 'Failed to load the document. Please try again.';
    this.docViewerLoaded = false;
  }

  retryLoading() {
    this.error = null;
    this.docViewerLoaded = false;
    // Force reload by updating the src
    const currentSrc = this.src;
    this.src = null;
    setTimeout(() => {
      this.src = currentSrc;
    }, 100);
  }

  downloadDocument() {
    if (!this.src) return;

    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = this.src;
    link.download = this.title || 'document';
    link.target = '_blank'; // Open in new tab
    link.rel = 'noopener noreferrer'; // Security best practice

    // Force download by setting the download attribute
    link.setAttribute('download', '');

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  printDocument() {
    if (!this.docViewerLoaded) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      console.error('Failed to open print window');
      return;
    }

    // Write the content to the new window
    printWindow.document.write(`
      <html>
        <head>
          <title>${this.title || 'Print Document'}</title>
          <style>
            body { margin: 0; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          ${
            this.viewer === 'url'
              ? `<img src="${this.src}" alt="${this.title}">`
              : `<iframe src="${this.src}" style="width: 100%; height: 100vh; border: none;"></iframe>`
          }
        </body>
      </html>
    `);

    // Close the document to finish loading
    printWindow.document.close();

    // Wait for the iframe/image to load before printing
    if (this.viewer === 'url') {
      const img = printWindow.document.querySelector('img');
      if (img) {
        img.onload = () => {
          printWindow.print();
          printWindow.onafterprint = () => printWindow.close();
        };
      }
    } else {
      const iframe = printWindow.document.querySelector('iframe');
      if (iframe) {
        iframe.onload = () => {
          printWindow.print();
          printWindow.onafterprint = () => printWindow.close();
        };
      }
    }
  }

  printAsPDF() {
    if (!this.docViewerLoaded) return;

    if (this.viewer === 'url') {
      // Handle image printing
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Enable CORS
      img.onload = () => {
        // Create a canvas with A4 dimensions (595 x 842 points at 72 DPI)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size to A4
        canvas.width = 595;
        canvas.height = 842;

        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Calculate dimensions to maintain aspect ratio
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);

        const width = img.width * scale;
        const height = img.height * scale;

        // Center the image
        const x = (canvas.width - width) / 2;
        const y = (canvas.height - height) / 2;

        // Draw the image
        ctx.drawImage(img, x, y, width, height);

        // Convert to PDF using browser's print functionality
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          console.error('Failed to open print window');
          return;
        }

        printWindow.document.write(`
          <html>
            <head>
              <title>${this.title || 'Print as PDF'}</title>
              <style>
                @page {
                  size: A4;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
                img {
                  width: 100%;
                  height: auto;
                }
              </style>
            </head>
            <body>
              <img src="${canvas.toDataURL('image/png')}" alt="${this.title}">
            </body>
          </html>
        `);

        printWindow.document.close();

        // Wait for the image to load before printing
        const printImg = printWindow.document.querySelector('img');
        if (printImg) {
          printImg.onload = () => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
          };
        }
      };

      img.onerror = () => {
        console.error('Failed to load image');
        this.error = 'Failed to load the image. Please try again.';
      };

      img.src = this.src;
    } else {
      // Handle document printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        console.error('Failed to open print window');
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>${this.title || 'Print as PDF'}</title>
            <style>
              @media print {
                body { margin: 0; }
                iframe { width: 100%; height: 100vh; border: none; }
              }
              @page {
                size: A4;
                margin: 0;
              }
            </style>
          </head>
          <body>
            <iframe src="${this.src}" style="width: 100%; height: 100vh; border: none;"></iframe>
          </body>
        </html>
      `);

      printWindow.document.close();

      const iframe = printWindow.document.querySelector('iframe');
      if (iframe) {
        iframe.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
          }, 500);
        };
      }
    }
  }

  preventContextMenu(event: MouseEvent) {
    event.preventDefault();
    return false;
  }

  getFileName(): string {
    if (!this.src) return '';
    if (this.title) return this.title;

    // Extract filename from URL
    const url = this.src.toString();
    const parts = url.split('/');
    return parts[parts.length - 1].split('?')[0];
  }

  getFileType(): string {
    if (!this.src) return '';

    // Get file extension
    const url = this.src.toString();
    const extension = url.split('.').pop()?.toLowerCase() || '';

    // Map common extensions to readable types
    const typeMap: { [key: string]: string } = {
      pdf: 'PDF Document',
      doc: 'Word Document',
      docx: 'Word Document',
      xls: 'Excel Spreadsheet',
      xlsx: 'Excel Spreadsheet',
      jpg: 'JPEG Image',
      jpeg: 'JPEG Image',
      png: 'PNG Image',
      gif: 'GIF Image',
      mp4: 'MP4 Video',
      txt: 'Text File',
      html: 'HTML Document',
      htm: 'HTML Document',
    };

    return typeMap[extension] || `${extension.toUpperCase()} File`;
  }

  getFileSize(): string {
    if (!this.info?.size) return '';

    const bytes = this.info.size;
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  formatDate(date: string | number | Date): string {
    if (!date) return '';

    const d = new Date(date);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
